import { useQuery } from "@tanstack/react-query";
import { readWorkspaceAddonsState } from "@/services/workspaceAddons";
import type { WorkspaceAddonsStateResponse } from "@/types";

/**
 * Workspace add-ons hooks — Add-ons panel state read.
 *
 * Source: docs/product/experience-and-ui.md § 8b Members & billing — Add-ons panel
 * (Pro-tier interactive only per L1037). Sub-A of
 * ENG-FE-MEMBERS-ADDONS-REGION; state read added by
 * ENG-TRUE-DEDUP-UX-POLISH B2 (the cards previously seeded from
 * hardcoded `useState` defaults and lied after every reload).
 *
 * The former `useToggleComplianceAddon` (the `PUT
 * /workspace/addons/compliance` wrapper) was REMOVED 2026-07-12
 * (ENG-TRUE-DEDUP-UX-POLISH compliance dual-path unification): the
 * canonical toggle is `useToggleComplianceAddon` in `useBilling.ts` —
 * `POST /api/v1/billing/addons/compliance` — which is payment-backed,
 * syncs `Workspace.compliance_addon_active` server-side, and
 * invalidates `WORKSPACE_ADDONS_STATE_KEY` on success so this read
 * re-seeds.
 *
 * The former `useToggleManagedImageAddon` was REMOVED 2026-07-09
 * (ENG-TRUE-DEDUP-UX-POLISH B4): the Managed Image Add-on card is
 * RETIRED per spec § 8b — managed image is metered on every plan.
 */

export const WORKSPACE_ADDONS_STATE_KEY = ["workspace-addons-state"] as const;

/** Persisted Add-ons panel state — seeds ComplianceAddonCard + RegionPinCard. */
export function useWorkspaceAddonsState(enabled: boolean = true) {
  return useQuery<WorkspaceAddonsStateResponse, Error>({
    queryKey: WORKSPACE_ADDONS_STATE_KEY,
    queryFn: readWorkspaceAddonsState,
    enabled,
    staleTime: 30_000,
  });
}
